/* KBF catalogue enhancements. Server-side facets replace client crawl. */
(() => {
  'use strict';
  const productCards = (scope) => [...scope.querySelectorAll('.pcard[data-product-id]')];

  if (!customElements.get('kbf-collection')) {
    customElements.define('kbf-collection', class extends HTMLElement {
      connectedCallback() {
        this.controller = new AbortController();
        this.panel = this.querySelector('[data-filter-panel]');
        this.toggle = this.querySelector('[data-filters-toggle]');
        this.status = this.querySelector('[data-catalog-status]');
        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-filters-toggle]')) this.openFilters(!this.panel.open);
          if (event.target.closest('[data-filters-done]')) this.openFilters(false);
          if (event.target === this.panel) {
            const bounds = this.panel.getBoundingClientRect();
            if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.openFilters(false);
          }
        }, { signal: this.controller.signal });
        this.addEventListener('change', (event) => {
          const url = event.target.closest('[data-filter-url]')?.dataset.filterUrl;
          if (!url) return;
          if (this.status) this.status.textContent = 'Updating products…';
          location.assign(url);
        }, { signal: this.controller.signal });
        this.panel.addEventListener('close', () => {
          this.toggle.setAttribute('aria-expanded', 'false');
          document.body.classList.remove('kbf-filters-open');
          this.toggle.focus();
        }, { signal: this.controller.signal });
        this.load();
      }

      disconnectedCallback() {
        this.controller.abort();
        if (this.panel.open) { this.panel.close(); document.body.classList.remove('kbf-filters-open'); }
      }
      openFilters(open) {
        if (open) this.panel.showModal();
        else this.panel.close();
        document.body.classList.toggle('kbf-filters-open', open);
        this.toggle.setAttribute('aria-expanded', String(open));
      }

      load() {
        this.sort = this.querySelector('[data-kbf-sort]');
        if (!this.sort) return;
        const currentURL = new URL(location.href);
        this.sort.value = ['price-ascending', 'price-descending'].includes(currentURL.searchParams.get('sort_by')) ? currentURL.searchParams.get('sort_by') : 'manual';
        this.page = 1;
        this.pageSize = Number(this.dataset.pageSize) || 24;
        this.ready = true;
        this.toggle.hidden = false;
        this.addEventListener('change', (event) => {
          if (!event.target.matches('[data-kbf-sort]')) return;
          this.paint();
        }, { signal: this.controller.signal });
        this.paint(); this.status.textContent = '';
      }

      paint() {
        const pages = Math.max(1, Math.ceil(productCards(this).length / this.pageSize));
        this.page = Math.max(1, Math.min(this.page, pages));
        const productGrid = this.querySelector('#product-grid');
        if (productGrid) productGrid.replaceChildren(...productCards(this).slice((this.page - 1) * this.pageSize, this.page * this.pageSize));
        const pagination = this.querySelector('[data-kbf-pagination]');
        if (!pagination) return;
        pagination.replaceChildren(); pagination.hidden = pages === 1;
        const go = (text, page, disabled) => {
          const button = document.createElement('button');
          button.type = 'button'; button.textContent = text; button.disabled = disabled;
          button.addEventListener('click', () => {
            this.page = page; this.paint();
            const toolbar = this.querySelector('.toolbar');
            if (toolbar) toolbar.scrollIntoView({ block: 'start' });
            this.toggle.focus({ preventScroll: true });
          });
          return button;
        };
        const pageLabel = document.createElement('span'); pageLabel.textContent = `Page ${this.page} of ${pages}`;
        pagination.append(go('Previous', this.page - 1, this.page === 1), pageLabel, go('Next', this.page + 1, this.page === pages));
      }
    });
  }

  if (!customElements.get('kbf-xsell')) {
    customElements.define('kbf-xsell', class extends HTMLElement {
      connectedCallback() { this.controller = new AbortController(); }
      disconnectedCallback() { this.controller.abort(); }
    });
  }

  // PDP gallery: thumbnails swap the main photo (owner 2026-09-29 — extra photos never showed).
  if (!window.kbfGalleryUI) {
    window.kbfGalleryUI = true;
    document.addEventListener('click', (event) => {
      const thumb = event.target.closest('.kbf-thumb');
      if (!thumb) return;
      const gallery = thumb.closest('[data-kbf-gallery]');
      const img = gallery?.querySelector('[data-kbf-product-image] img');
      if (!img) return;
      img.srcset = thumb.dataset.srcset;
      img.src = thumb.dataset.src;
      img.style.setProperty('--w', `${thumb.dataset.w}px`);
      gallery.querySelectorAll('.kbf-thumb').forEach((t) => t.setAttribute('aria-pressed', String(t === thumb)));
    });
  }

  // PDP zoom viewer: tap the photo -> full screen; pinch / wheel / +- zoom, drag pans (owner 2026-10-29 — small label text unreadable).
  if (!window.kbfZoomUI) {
    window.kbfZoomUI = true;
    const MAX = 5;
    let dlg, stage, img, list = [], idx = 0, s = 1, x = 0, y = 0, prevD = 0, drag = null, moved = false, lastTap = 0;
    const pts = new Map();
    const clamp = () => {
      s = Math.min(MAX, Math.max(1, s));
      const mx = (img.offsetWidth * (s - 1)) / 2, my = (img.offsetHeight * (s - 1)) / 2;
      x = Math.min(mx, Math.max(-mx, x));
      y = Math.min(my, Math.max(-my, y));
      img.style.transform = `translate(${x}px, ${y}px) scale(${s})`;
      stage.style.cursor = s > 1 ? 'grab' : 'zoom-in';
    };
    const zoomAt = (next, cx, cy) => {
      const r = stage.getBoundingClientRect();
      const px = cx - r.left - r.width / 2, py = cy - r.top - r.height / 2;
      next = Math.min(MAX, Math.max(1, next));
      x = px - ((px - x) * next) / s;
      y = py - ((py - y) * next) / s;
      s = next;
      clamp();
    };
    const center = () => { const r = stage.getBoundingClientRect(); return [r.left + r.width / 2, r.top + r.height / 2]; };
    const show = (i) => {
      idx = (i + list.length) % list.length;
      s = 1; x = 0; y = 0;
      img.src = list[idx];
      clamp();
      const count = dlg.querySelector('[data-kz-count]');
      if (count) count.textContent = `${idx + 1} / ${list.length}`;
    };
    const bind = () => {
      stage.addEventListener('wheel', (e) => { e.preventDefault(); zoomAt(s * Math.exp(-e.deltaY * 0.002), e.clientX, e.clientY); }, { passive: false });
      stage.addEventListener('pointerdown', (e) => {
        stage.setPointerCapture(e.pointerId);
        pts.set(e.pointerId, [e.clientX, e.clientY]);
        moved = false;
        if (pts.size === 2) { const [a, b] = [...pts.values()]; prevD = Math.hypot(a[0] - b[0], a[1] - b[1]); drag = null; }
        else drag = [e.clientX - x, e.clientY - y];
      });
      stage.addEventListener('pointermove', (e) => {
        if (!pts.has(e.pointerId)) return;
        pts.set(e.pointerId, [e.clientX, e.clientY]);
        if (pts.size === 2) {
          const [a, b] = [...pts.values()];
          const d = Math.hypot(a[0] - b[0], a[1] - b[1]);
          if (prevD) zoomAt(s * (d / prevD), (a[0] + b[0]) / 2, (a[1] + b[1]) / 2);
          prevD = d; moved = true;
        } else if (drag && s > 1) {
          x = e.clientX - drag[0]; y = e.clientY - drag[1]; moved = true; clamp();
        }
      });
      const up = (e) => {
        pts.delete(e.pointerId);
        if (pts.size === 1) { const [p] = pts.values(); drag = [p[0] - x, p[1] - y]; prevD = 0; return; }
        drag = null; prevD = 0;
        if (moved || e.type === 'pointercancel') return;
        const now = Date.now();
        if (now - lastTap < 320) { zoomAt(s > 1 ? 1 : 2.5, e.clientX, e.clientY); lastTap = 0; } else lastTap = now;
      };
      stage.addEventListener('pointerup', up);
      stage.addEventListener('pointercancel', up);
      dlg.addEventListener('click', (e) => {
        const act = e.target.closest('[data-kz]')?.dataset.kz;
        if (act === 'close') dlg.close();
        else if (act === 'next') show(idx + 1);
        else if (act === 'prev') show(idx - 1);
        else if (act === 'in') zoomAt(s * 1.6, ...center());
        else if (act === 'out') zoomAt(s / 1.6, ...center());
      });
      dlg.addEventListener('keydown', (e) => {
        if (list.length > 1 && e.key === 'ArrowRight') show(idx + 1);
        if (list.length > 1 && e.key === 'ArrowLeft') show(idx - 1);
      });
      window.addEventListener('resize', () => dlg.open && clamp());
    };
    document.addEventListener('click', (event) => {
      if (!event.target.closest('[data-kbf-zoom-open], [data-kbf-product-image] img')) return;
      const gallery = event.target.closest('[data-kbf-gallery]');
      const next = gallery?.querySelector('[data-kbf-zoom]');
      if (!next) return;
      if (next !== dlg) {
        dlg = next; stage = dlg.querySelector('[data-kz-stage]'); img = dlg.querySelector('[data-kz-img]');
        list = JSON.parse(gallery.querySelector('[data-kbf-zoom-list]').textContent);
        bind();
      }
      const thumbs = [...gallery.querySelectorAll('.kbf-thumb')];
      dlg.showModal();
      show(Math.max(0, thumbs.findIndex((t) => t.getAttribute('aria-pressed') === 'true')));
    });
  }

  if (!window.kbfProductVariantUI && typeof subscribe === 'function') {
    window.kbfProductVariantUI = true;
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data: { sectionId, html } }) => {
      const product = document.getElementById(`MainProduct-${sectionId}`);
      if (!product?.hasAttribute('data-kbf-product')) return;
      const source = html.querySelector('[data-kbf-product-image]');
      const target = product.querySelector('[data-kbf-product-image]');
      if (source && target) { target.innerHTML = source.innerHTML; target.className = source.className; }
      html.querySelectorAll('.kbf-thumb').forEach((t) => {
        const own = product.querySelector(`.kbf-thumb[data-media-id="${t.dataset.mediaId}"]`);
        if (own) own.setAttribute('aria-pressed', t.getAttribute('aria-pressed'));
      });
      if (typeof product.setQuantityBoundries === 'function') product.setQuantityBoundries();
      const button = product.querySelector('[name="add"]');
      if (button && !button.disabled) {
        const span = button.querySelector('span');
        if (span) span.textContent = 'Add to bag';
      }
    });
  }
})();
